import { Injectable, Optional } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NavigationEnd, Router } from '@angular/router';
import * as Sentry from '@sentry/browser';
import { NgxLoggerLevel } from 'ngx-logger';
import { firstValueFrom } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { TaskCm } from '../../data/models';
import { TaskChangeSetContainer } from '../../data/models/task-change-set-container';
import { TaskQuery } from '../../task/task-query.service';
import { UserService } from '../../user/user.service';
import { getClientTraceContext } from '../../tracing/w3c-trace-context';
import { lastPlanningEntityTrace } from '../../tracing/planning-entity-trace';
import { registerPlanningDiagnosticRecorder } from './planning-diagnostic-bridge';
import { createPlanningReportId, planningSessionId } from './planning-session';
import {
  describeTaskMutation,
  IMMEDIATE_CORRECTION_MS,
  isJumpAnomaly,
  LastCanvasMoveEnd,
  PINNED_DIAGNOSTIC_CATEGORIES,
  PlanningDateNavSource,
  PlanningDiagnosticCategory,
  PlanningDiagnosticEvent,
  PlanningJumpReport,
  PlanningNotificationMetadata,
  PlanningProblemType,
  stringifyDiagnosticTime,
  summarizeTask,
  TaskPositionSnapshot,
} from './planning-diagnostics.types';

export interface PlanningDiagnosticsConfig {
  maxAgeMs: number;
  maxEvents: number;
  enabled: boolean;
  now: () => number;
}

export class PlanningFeedbackDeliveryError extends Error {
  constructor() {
    super('sentry-feedback-failed');
    this.name = 'PlanningFeedbackDeliveryError';
  }
}

const DEFAULT_MAX_AGE_MS = 5 * 60 * 1000;
const DEFAULT_MAX_EVENTS = 500;
const RESIZE_DEBOUNCE_MS = 250;
const DATE_REINIT_OVERLAP_MS = 8 * 1000;

/**
 * In-memory ring buffer of planning events. Collects locally; nothing is sent
 * until {@link submitReport} (the "Sprung melden" button), which uploads via the API.
 */
@Injectable({
  providedIn: 'root',
})
export class PlanningDiagnosticsService {
  readonly sessionId = planningSessionId;
  private readonly ring: PlanningDiagnosticEvent[] = [];
  private readonly pinned: PlanningDiagnosticEvent[] = [];
  private seq = 0;
  private readonly inflightByTask = new Map<number, number>();
  private canvasAction: { active: boolean; taskId?: string } = { active: false };
  private maxAgeMs: number;
  private maxEvents: number;
  private now: () => number;
  private enabled: boolean;
  private startedAtMs: number;
  private resizeTimer: ReturnType<typeof setTimeout> | null = null;
  private lastNavUrl?: string;
  private pendingReinitReasons: string[] = [];
  private reinitInFlight = false;
  private lastDateNav: { t: number; source: PlanningDateNavSource } | null = null;
  private lastMoveStartByTask = new Map<number, LastCanvasMoveEnd>();
  private lastMoveEnd: LastCanvasMoveEnd | null = null;

  constructor(
    @Optional() private userService?: UserService,
    @Optional() private http?: HttpClient,
    @Optional() private router?: Router,
    @Optional() private taskQuery?: TaskQuery,
  ) {
    this.configure({
      maxAgeMs: DEFAULT_MAX_AGE_MS,
      maxEvents: DEFAULT_MAX_EVENTS,
      now: () => Date.now(),
      enabled: environment.enablePlanningDiagnostics,
    });
    this.startedAtMs = this.now();
    registerPlanningDiagnosticRecorder((category, data) => this.record(category, data));
    this.attachCollectors();
  }

  /** Test-only factory — Angular DI uses the default constructor. */
  static forTests(
    config: Partial<PlanningDiagnosticsConfig> = {},
    deps: {
      http?: { post: (...args: unknown[]) => unknown };
      taskQuery?: Pick<TaskQuery, 'getCount'>;
    } = {},
  ): PlanningDiagnosticsService {
    const service = new PlanningDiagnosticsService(
      undefined,
      deps.http as HttpClient | undefined,
      undefined,
      deps.taskQuery as TaskQuery | undefined,
    );
    service.detachCollectors();
    service.configure({
      maxAgeMs: config.maxAgeMs ?? DEFAULT_MAX_AGE_MS,
      maxEvents: config.maxEvents ?? DEFAULT_MAX_EVENTS,
      now: config.now ?? (() => Date.now()),
      enabled: config.enabled ?? true,
    });
    service.startedAtMs = service.now();
    return service;
  }

  private configure(config: PlanningDiagnosticsConfig): void {
    this.maxAgeMs = config.maxAgeMs;
    this.maxEvents = config.maxEvents;
    this.now = config.now;
    this.enabled = config.enabled;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  record(
    cat: PlanningDiagnosticCategory,
    data: Record<string, unknown> = {},
    taskId?: number,
  ): void {
    if (!this.enabled) {
      return;
    }
    const t = this.now();
    const trace = getClientTraceContext();
    const event: PlanningDiagnosticEvent = {
      t,
      seq: ++this.seq,
      cat,
      taskId,
      data: {
        ...data,
        canvasAction: { ...this.canvasAction },
        traceId: trace.traceId,
        spanId: trace.spanId,
        traceparent: trace.traceparent,
      },
    };
    if (PINNED_DIAGNOSTIC_CATEGORIES.has(cat)) {
      this.pinned.push(event);
    } else {
      this.ring.push(event);
    }
    this.prune(t);
  }

  setCanvasAction(active: boolean, taskId?: string): void {
    if (this.canvasAction.active === active && this.canvasAction.taskId === taskId) {
      return;
    }
    this.canvasAction = { active, taskId };
    this.record(
      'canvas.action',
      { phase: active ? 'start' : 'end', taskId },
      Number(taskId) || undefined,
    );
  }

  recordMove(
    phase: 'start' | 'end',
    payload: Record<string, unknown>,
    taskId?: number,
  ): void {
    const from = stringifyDiagnosticTime(payload['from']) ?? (payload['from'] as string);
    const to = stringifyDiagnosticTime(payload['to']) ?? (payload['to'] as string);
    const employeeId = String(
      payload['employeeIdFromBar'] ?? payload['employeeId'] ?? '',
    );
    const t = this.now();
    const extra: Record<string, unknown> = {};
    if (phase === 'start' && taskId != null && employeeId && from && to) {
      this.lastMoveStartByTask.set(taskId, {
        taskId,
        t,
        employeeId,
        from,
        to,
      });
    }
    if (phase === 'end' && taskId != null) {
      const start = this.lastMoveStartByTask.get(taskId);
      const previousEnd = this.lastMoveEnd;
      extra['immediateCorrection'] =
        previousEnd?.taskId === taskId && t - previousEnd.t <= IMMEDIATE_CORRECTION_MS;
      if (employeeId && from && to) {
        this.lastMoveEnd = {
          taskId,
          t,
          employeeId,
          from,
          to,
          mappingMismatch: payload['mappingMismatch'] === true,
          start: start
            ? {
                employeeId: start.employeeId,
                from: start.from,
                to: start.to,
              }
            : undefined,
        };
      }
    }
    this.record('canvas.move', { phase, ...payload, ...extra }, taskId);
  }

  getLastMoveEnd(): LastCanvasMoveEnd | null {
    return this.lastMoveEnd;
  }

  recordPollBatch(count: number): void {
    if (count <= 0) {
      return;
    }
    this.record('store.upsert', {
      source: 'poll',
      batch: true,
      count,
      before: null,
    });
  }

  recordCanvasReinit(
    phase: 'request' | 'start' | 'end' | 'error',
    reasons: string[],
    extra: Record<string, unknown> = {},
  ): void {
    if (phase === 'request') {
      this.pendingReinitReasons.push(...reasons);
    } else if (phase === 'start') {
      this.reinitInFlight = true;
    } else if (phase === 'end' || phase === 'error') {
      this.reinitInFlight = false;
      this.pendingReinitReasons = [];
    }
    const msSinceDateNav = this.lastDateNav ? this.now() - this.lastDateNav.t : null;
    const overlapDate =
      msSinceDateNav != null && msSinceDateNav <= DATE_REINIT_OVERLAP_MS;
    this.record('canvas.reinit', {
      phase,
      reasons,
      pendingReinit: [...this.pendingReinitReasons],
      reinitInFlight: this.reinitInFlight,
      msSinceDateNav,
      lastDateNavSource: this.lastDateNav?.source ?? null,
      overlapDate,
      overlapMove: this.canvasAction.active,
      ...extra,
    });
    if (overlapDate && (phase === 'request' || phase === 'start')) {
      this.record('anomaly.overlap', {
        kind: 'reinit-after-date',
        phase,
        reasons,
        msSinceDateNav,
        lastDateNavSource: this.lastDateNav?.source,
      });
    }
    if (this.canvasAction.active && (phase === 'request' || phase === 'start')) {
      this.record('anomaly.overlap', {
        kind: 'reinit-during-move',
        phase,
        reasons,
        taskId: this.canvasAction.taskId,
      });
    }
  }

  recordDateNav(
    source: PlanningDateNavSource,
    phase: 'start' | 'end' | 'error',
    extra: Record<string, unknown> = {},
  ): void {
    if (phase === 'start') {
      this.lastDateNav = { t: this.now(), source };
    }
    const overlapMove = this.canvasAction.active;
    const overlapReinit = this.reinitInFlight || this.pendingReinitReasons.length > 0;
    this.record('canvas.date', {
      source,
      phase,
      overlapMove,
      overlapReinit,
      pendingReinit: [...this.pendingReinitReasons],
      reinitInFlight: this.reinitInFlight,
      ...extra,
    });
    if (phase === 'start' && (overlapMove || overlapReinit)) {
      this.record('anomaly.overlap', {
        kind: overlapMove
          ? overlapReinit
            ? 'date-during-move-and-reinit'
            : 'date-during-move'
          : 'date-during-reinit',
        source,
        pendingReinit: [...this.pendingReinitReasons],
      });
    }
  }

  recordViewportLoad(extra: Record<string, unknown> = {}): void {
    const recentDate = this.isRecentDateNav();
    const reinitBusy = this.isReinitBusy();
    if (!recentDate && !reinitBusy) {
      return;
    }
    this.record('canvas.viewport', {
      relatedTo: recentDate ? 'date' : 'reinit',
      lastDateNavSource: this.lastDateNav?.source ?? null,
      msSinceDateNav: this.lastDateNav ? this.now() - this.lastDateNav.t : null,
      pendingReinit: [...this.pendingReinitReasons],
      reinitInFlight: this.reinitInFlight,
      ...extra,
    });
  }

  isRecentDateNav(maxAgeMs = DATE_REINIT_OVERLAP_MS): boolean {
    return this.lastDateNav != null && this.now() - this.lastDateNav.t <= maxAgeMs;
  }

  isReinitBusy(): boolean {
    return this.reinitInFlight || this.pendingReinitReasons.length > 0;
  }

  recordWindowResize(): void {
    if (typeof window === 'undefined') {
      return;
    }
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    this.resizeTimer = setTimeout(() => {
      this.resizeTimer = null;
      this.record('ui.resize', {
        source: 'window',
        innerWidth: window.innerWidth,
        innerHeight: window.innerHeight,
      });
    }, RESIZE_DEBOUNCE_MS);
  }

  recordNavigation(url: string, urlAfterRedirects?: string): void {
    this.record('router.navigation', {
      url,
      urlAfterRedirects: urlAfterRedirects ?? url,
      previousUrl: this.lastNavUrl ?? null,
    });
    this.lastNavUrl = urlAfterRedirects ?? url;
  }

  recordLoggerError(entry: {
    level: number;
    message?: unknown;
    additional?: unknown[];
    fileName?: string;
    lineNumber?: number;
    timestamp?: string;
  }): void {
    if (entry.level < NgxLoggerLevel.ERROR) {
      return;
    }
    this.record('log.error', {
      level: NgxLoggerLevel[entry.level] ?? entry.level,
      message: this.stringifyLogValue(entry.message),
      additional: (entry.additional ?? []).map((value) => this.stringifyLogValue(value)),
      fileName: entry.fileName,
      lineNumber: entry.lineNumber,
      logTimestamp: entry.timestamp,
    });
  }

  recordLock(action: 'acquire' | 'release', employeeId: string | undefined | null): void {
    if (!employeeId) {
      return;
    }
    this.record('lock.employee', { action, employeeId });
  }

  recordRescheduleStart(task: TaskCm): void {
    const n = (this.inflightByTask.get(task.id) ?? 0) + 1;
    this.inflightByTask.set(task.id, n);
    this.record(
      'task.reschedule',
      { phase: 'start', inflight: n, task: summarizeTask(task) },
      task.id,
    );
  }

  recordRescheduleEnd(
    taskId: number,
    phase: 'ok' | 'error',
    extra: Record<string, unknown> = {},
  ): void {
    const n = Math.max(0, (this.inflightByTask.get(taskId) ?? 1) - 1);
    if (n === 0) {
      this.inflightByTask.delete(taskId);
    } else {
      this.inflightByTask.set(taskId, n);
    }
    this.record('task.reschedule', { phase, inflight: n, ...extra }, taskId);
  }

  recordHubNotification(message: TaskChangeSetContainer): void {
    const metadata = message as TaskChangeSetContainer & PlanningNotificationMetadata;
    const changeSet = message.taskChangeSetCm;
    const tasks = (changeSet?.added ?? []).concat(changeSet?.updated ?? []);
    this.record('hub.notification', {
      notificationId: metadata.notificationId,
      producer: metadata.producer,
      emittedAtUtc: metadata.emittedAtUtc,
      originTraceId: metadata.originTraceId,
      originSpanId: metadata.originSpanId,
      requestedProjectIds: (metadata.requestedProjectIds ?? []).slice(0, 50),
      loadedProjectIds: (metadata.loadedProjectIds ?? []).slice(0, 50),
      missingProjectIds: (metadata.missingProjectIds ?? []).slice(0, 50),
      updateTimestamp: changeSet?.updateTimestamp,
      deleted: changeSet?.deleted ?? [],
      tasks: tasks.map(summarizeTask),
    });
  }

  recordVisibleRows(employeeIds: string[]): void {
    this.record('rows.visible', { employeeIds, count: employeeIds.length });
  }

  recordTaskMutation(
    source: string,
    before: TaskCm | null | undefined,
    after: TaskCm | null | undefined,
    extra: Record<string, unknown> = {},
  ): void {
    const beforeSnap = summarizeTask(before);
    const afterSnap = summarizeTask(after);
    if (!this.positionChanged(beforeSnap, afterSnap) && extra['applied'] !== false) {
      return;
    }
    const mutation = describeTaskMutation(beforeSnap, afterSnap);
    const taskId = after?.id ?? before?.id;
    const overwriteDuringMove =
      this.canvasAction.active &&
      this.canvasAction.taskId != null &&
      String(taskId) === this.canvasAction.taskId &&
      source !== 'optimistic';
    const lastMoveEnd =
      taskId != null && this.lastMoveEnd?.taskId === taskId ? this.lastMoveEnd : null;
    this.record(
      'store.upsert',
      {
        source,
        ...mutation,
        overwriteDuringMove,
        ...extra,
      },
      taskId,
    );
    if (isJumpAnomaly(source, mutation, { overwriteDuringMove, lastMoveEnd })) {
      this.record(
        'anomaly.jump',
        {
          source,
          ...mutation,
          overwriteDuringMove,
        },
        taskId,
      );
    }
  }

  recordSkipStale(source: string, existing: TaskCm, incoming: TaskCm): void {
    this.record(
      'store.skip-stale',
      {
        source,
        ...describeTaskMutation(summarizeTask(existing), summarizeTask(incoming)),
      },
      incoming.id,
    );
  }

  exportSnapshot(
    extras: Record<string, unknown> = {},
    problemType: PlanningProblemType = 'jump',
  ): PlanningJumpReport {
    const t = this.now();
    this.prune(t);
    const inflightReschedules: Record<string, number> = {};
    this.inflightByTask.forEach((count, id) => {
      inflightReschedules[String(id)] = count;
    });
    return {
      reportId: createPlanningReportId(),
      problemType,
      createdAt: new Date(t).toISOString(),
      sessionId: this.sessionId,
      version: environment.version,
      envName: environment.envName,
      userName: this.userService?.currentUser?.userName,
      startedAt: new Date(this.startedAtMs).toISOString(),
      uptimeMs: Math.max(0, t - this.startedAtMs),
      taskCount: this.taskQuery?.getCount() ?? null,
      canvasAction: { ...this.canvasAction },
      inflightReschedules,
      extras: {
        ...extras,
        trace: getClientTraceContext(),
        lastMutation: lastPlanningEntityTrace() ?? null,
        lastMoveEnd: this.lastMoveEnd,
        lastDateNav: this.lastDateNav
          ? {
              source: this.lastDateNav.source,
              at: new Date(this.lastDateNav.t).toISOString(),
              msAgo: t - this.lastDateNav.t,
            }
          : null,
        reinitInFlight: this.reinitInFlight,
        pendingReinit: [...this.pendingReinitReasons],
      },
      events: this.allEvents(),
    };
  }

  async submitReport(extras: Record<string, unknown> = {}): Promise<PlanningJumpReport> {
    if (!this.http) {
      throw new Error('HttpClient is required to submit a jump report');
    }
    const snapshot = this.exportSnapshot(extras);
    await firstValueFrom(
      this.http.post(
        `${environment.serverApiUrl}/api/PlanningJumpReport/Submit`,
        snapshot,
      ),
    );
    this.tryCaptureSentry(snapshot);
    await this.sendSentryFeedback(snapshot);
    return snapshot;
  }

  async submitProblemReport(
    problemType: PlanningProblemType,
    extras: Record<string, unknown> = {},
    screenshot?: { blob: Blob; width: number; height: number; capturedAt: string } | null,
  ): Promise<PlanningJumpReport> {
    if (!this.http) {
      throw new Error('HttpClient is required to submit a planning problem report');
    }
    const snapshot = this.exportSnapshot(
      {
        ...extras,
        screenshot: screenshot
          ? {
              capturedAt: screenshot.capturedAt,
              width: screenshot.width,
              height: screenshot.height,
            }
          : { available: false },
      },
      problemType,
    );
    const body = new FormData();
    body.append('report', JSON.stringify(snapshot));
    if (screenshot) {
      body.append('screenshot', screenshot.blob, `${snapshot.reportId}.png`);
    }
    await firstValueFrom(
      this.http.post(
        `${environment.serverApiUrl}/api/PlanningProblemReport/Submit`,
        body,
      ),
    );
    this.tryCaptureSentry(snapshot);
    await this.sendSentryFeedback(snapshot, screenshot);
    return snapshot;
  }

  /** Exposed for tests. */
  getEvents(): PlanningDiagnosticEvent[] {
    return this.allEvents();
  }

  /** Test helper: drop window listeners created by the default constructor. */
  detachCollectors(): void {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onWindowResize);
    }
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = null;
    }
  }

  private attachCollectors(): void {
    if (!this.enabled) {
      return;
    }
    this.router?.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => this.recordNavigation(event.url, event.urlAfterRedirects));
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onWindowResize);
    }
  }

  private readonly onWindowResize = (): void => {
    this.recordWindowResize();
  };

  private stringifyLogValue(value: unknown): string {
    if (value == null) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (value instanceof Error) {
      return value.stack ?? value.message;
    }
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  private positionChanged(
    before: TaskPositionSnapshot | null,
    after: TaskPositionSnapshot | null,
  ): boolean {
    if (!before || !after) {
      return true;
    }
    return (
      before.employeeId !== after.employeeId ||
      before.from !== after.from ||
      before.to !== after.to ||
      before.timestamp !== after.timestamp
    );
  }

  private allEvents(): PlanningDiagnosticEvent[] {
    return [...this.pinned, ...this.ring].sort((a, b) => a.seq - b.seq);
  }

  private prune(now: number): void {
    this.pruneBuffer(this.ring, now);
    this.pruneBuffer(this.pinned, now);
  }

  private pruneBuffer(buffer: PlanningDiagnosticEvent[], now: number): void {
    const cutoff = now - this.maxAgeMs;
    while (buffer.length > 0 && buffer[0].t < cutoff) {
      buffer.shift();
    }
    while (buffer.length > this.maxEvents) {
      buffer.shift();
    }
  }

  private captureSentry(snapshot: PlanningJumpReport): void {
    if (!environment.sentry) {
      return;
    }
    const anomalies = snapshot.events.filter((e) => e.cat === 'anomaly.jump');
    const trace = snapshot.extras['trace'] as
      | { traceId?: string; spanId?: string; traceparent?: string }
      | undefined;
    Sentry.withScope((scope) => {
      scope.setTag('reportId', snapshot.reportId);
      scope.setTag('sessionId', snapshot.sessionId);
      if (trace?.traceId) {
        scope.setTag('trace_id', trace.traceId);
      }
      if (trace?.spanId) {
        scope.setTag('span_id', trace.spanId);
      }
      scope.setTag('problemType', snapshot.problemType);
      Sentry.captureMessage('planning-problem-report', {
        level: 'info',
        extra: {
          reportId: snapshot.reportId,
          sessionId: snapshot.sessionId,
          eventCount: snapshot.events.length,
          anomalyCount: anomalies.length,
          canvasAction: snapshot.canvasAction,
          inflightReschedules: snapshot.inflightReschedules,
          uptimeMs: snapshot.uptimeMs,
          taskCount: snapshot.taskCount,
          traceId: trace?.traceId,
          spanId: trace?.spanId,
          traceparent: trace?.traceparent,
          lastMutation: snapshot.extras['lastMutation'],
          classification: snapshot.extras['classification'],
          jump: snapshot.extras['jump'],
          crash: snapshot.extras['crash'],
          detachedMove: snapshot.extras['detachedMove'],
          rendererErrors: snapshot.extras['rendererErrors'],
        },
      });
    });
  }

  /**
   * User Feedback in addition to the emailed server report. The email stays
   * the delivered report when this session has no Sentry client.
   */
  private async sendSentryFeedback(
    snapshot: PlanningJumpReport,
    screenshot?: { blob: Blob; width: number; height: number; capturedAt: string } | null,
  ): Promise<void> {
    const client = Sentry.getClient();
    if (!client) {
      return;
    }
    const message = feedbackMessage(snapshot);
    const attachments = await feedbackAttachments(snapshot, screenshot);
    try {
      Sentry.captureFeedback(
        {
          message,
          name: this.userService?.currentUser?.displayName || snapshot.userName,
          email: this.userService?.currentUser?.email,
          url: typeof location !== 'undefined' ? location.href : undefined,
          source: 'planning-problem-report',
        },
        { attachments },
      );
      const flushed = (await client.flush(2000)) ?? false;
      if (!flushed) {
        throw new PlanningFeedbackDeliveryError();
      }
    } catch (error) {
      if (error instanceof PlanningFeedbackDeliveryError) {
        throw error;
      }
      throw new PlanningFeedbackDeliveryError();
    }
  }

  private tryCaptureSentry(snapshot: PlanningJumpReport): void {
    try {
      this.captureSentry(snapshot);
    } catch {
      // The server already accepted the report; optional Sentry enrichment cannot fail it.
    }
  }
}

function feedbackMessage(snapshot: PlanningJumpReport): string {
  const jump = snapshot.extras['jump'] as { comment?: string } | undefined;
  const crash = snapshot.extras['crash'] as
    | { action?: string; outcome?: string }
    | undefined;
  const detached = snapshot.extras['detachedMove'] as
    | { taskId?: number; choice?: string; objectFrom?: string; pointerFrom?: string }
    | undefined;
  const statement =
    (detached
      ? `Verschieben vom Cursor gelöst (Task ${detached.taskId}): Objekt ${detached.objectFrom}, ` +
        `Cursor ${detached.pointerFrom}, Entscheid: ${detached.choice}`
      : '') ||
    jump?.comment?.trim() ||
    [crash?.action, crash?.outcome].filter((part) => !!part?.trim()).join('\n');
  const renderer = stringList(snapshot.extras['rendererErrors']);
  const body = statement || `${snapshot.problemType} report ${snapshot.reportId}`;
  if (renderer.length === 0) {
    return body;
  }
  return `${body}\n\n${renderer.map((line) => `- ${line}`).join('\n')}`;
}

async function feedbackAttachments(
  snapshot: PlanningJumpReport,
  screenshot?: { blob: Blob } | null,
): Promise<Array<{ filename: string; data: Uint8Array; contentType: string }>> {
  const attachments: Array<{ filename: string; data: Uint8Array; contentType: string }> =
    [];
  if (screenshot) {
    attachments.push({
      filename: `${snapshot.reportId}.png`,
      data: await bytesFromBlob(screenshot.blob),
      contentType: 'image/png',
    });
  }
  const renderer = stringList(snapshot.extras['rendererErrors']);
  const client = snapshot.events
    .filter(
      (event) =>
        event.cat === 'log.error' ||
        event.cat === 'graphics.error' ||
        event.cat === 'app.unhandled-error',
    )
    .map((event) => ({
      cat: event.cat,
      message: event.data['message'] ?? event.data['phase'] ?? null,
    }))
    .slice(-20);
  if (renderer.length > 0 || client.length > 0) {
    attachments.push({
      filename: 'error-messages.json',
      data: encodeUtf8(JSON.stringify({ renderer, client })),
      contentType: 'application/json',
    });
  }
  return attachments;
}

function encodeUtf8(value: string): Uint8Array {
  if (typeof TextEncoder !== 'undefined') {
    return new TextEncoder().encode(value);
  }
  const binary = unescape(encodeURIComponent(value));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function bytesFromBlob(blob: Blob): Promise<Uint8Array> {
  const withBuffer = blob as Blob & { arrayBuffer?: () => Promise<ArrayBuffer> };
  if (typeof withBuffer.arrayBuffer === 'function') {
    return withBuffer.arrayBuffer().then((buffer) => new Uint8Array(buffer));
  }
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result as ArrayBuffer));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
}

function stringList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
    .slice(0, 20);
}
